import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useRole } from 'entities/User';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { Table } from 'shared/ui/Table';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { ReceiptDocument, ReceiptStatus, useLazyGetReceiptsByContractIdQuery } from 'entities/Receipt';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';

interface ContractReceiptActionsProps {
  contractId: string;
}

// Full-payment (advance-offset, "closing") receipts have no Payment behind them - they're issued
// per service period, not per payment - so they never show up in the payment-level ReceiptActions
// history. This is the contract-level equivalent, shown both to staff and to the customer the
// receipts are actually issued to/for - but with a different level of detail: staff get every
// attempt including failures and the raw ATOL error (retries are automatic and internal, but
// useful for diagnosing a stuck one), the customer only ever sees the receipts that actually went
// through - a failed/retrying attempt is our problem to fix, not something to surface to them.
export const ContractReceiptActions: FC<ContractReceiptActionsProps> = memo(({ contractId }) => {
  const { t } = useAppTranslation(['contracts', 'common', 'payment']);
  const { isCustomer } = useRole();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [fetchReceipts, { data: receipts }] = useLazyGetReceiptsByContractIdQuery();

  const openModal = useCallback(async (): Promise<void> => {
    try {
      setIsModalOpen(true);
      await fetchReceipts(contractId).unwrap();
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Failed to load receipts'));
    }
  }, [fetchReceipts, contractId, t]);

  const closeModal = useCallback((): void => {
    setIsModalOpen(false);
  }, []);

  const visibleReceipts = useMemo(
    () => (isCustomer ? (receipts || []).filter((receipt) => receipt.status === ReceiptStatus.DONE) : receipts || []),
    [receipts, isCustomer],
  );

  const receiptUrlColumn = {
    title: '',
    dataIndex: 'receiptUrl',
    key: 'receiptUrl',
    render: (val: string) => {
      if (val) {
        return (
          <div className="cursor-pointer flex items-center justify-center" onClick={() => window.open(val, '_blank')}>
            <DocumentIcon /> <span className="ml-2 text-success">{t('Show receipt', { ns: 'payment' })}</span>
          </div>
        );
      }

      return null;
    },
  };

  const periodColumn = {
    title: t('Period ending'),
    dataIndex: 'periodEndDate',
    key: 'periodEndDate',
    render: (val: string) => (val ? getClientDateFormat(val) : '-'),
  };

  // customer view: just what they can act on (when it's for, where to view it) - no status/error
  // noise about attempts that never actually resulted in a fiscal document
  const columns = isCustomer
    ? [
        periodColumn,
        {
          title: t('Created', { ns: 'payment' }),
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (val: string) => getClientDateFormat(val),
        },
        receiptUrlColumn,
      ]
    : [
        periodColumn,
        { title: t('Status', { ns: 'payment' }), dataIndex: 'status', key: 'status', render: (val: string) => t(val, { ns: 'payment' }) },
        {
          title: t('Created', { ns: 'payment' }),
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (val: string) => getClientDateFormat(val),
        },
        {
          title: t('Error'),
          dataIndex: 'error',
          key: 'error',
          render: (val: CustomAny) => val?.text || val || '-',
          width: '30%',
        },
        receiptUrlColumn,
      ];

  const { isDesktop } = useScreenBreakpoints();

  const highlightRow = (record: ReceiptDocument): string => {
    return classNames({
      'bg-errorLight': record.status === ReceiptStatus.FAILED,
      'bg-successLight': record.status === ReceiptStatus.DONE,
      'bg-primaryLight': record.status === ReceiptStatus.PENDING,
    });
  };

  return (
    <>
      <Button theme="outlined" onClick={openModal}>
        {t(isCustomer ? 'Мои чеки' : 'История чеков полного расчёта')}
      </Button>

      <Modal isOpen={isModalOpen} onClose={closeModal} width={isDesktop ? 980 : 380}>
        <Table
          data={visibleReceipts}
          columns={columns}
          rowKey="receiptId"
          size="small"
          rowClassName={isCustomer ? undefined : highlightRow}
        />
      </Modal>
    </>
  );
});
